import { useLazyQuery, useMutation } from '@apollo/client';
import Restart from '@clearkit/icons/glyphs/Restart';
import Warning from '@clearkit/icons/glyphs/Warning';
import Zap from '@clearkit/icons/glyphs/Zap';
import {
  CKButton,
  CKButtonVariant,
  CKButtonVariantColor,
} from '@clearkit/react';

import LoadingBar, { useLoadingBarState } from '~/components/LoadingBar';
import {
  audienceHealth,
  audienceHealthLabel,
  readableAudienceEstimatedSize,
} from '~/lib/audiences';
import HealthIndicator from '~/pages/audiences/components/HealthIndicator/base';

import mutation from './mutation.graphql';
import query from './query.graphql';

const getAudienceType = (subtype) => {
  // TODO: Consider this when building Google Contact Audiences
  return `facebook_${subtype}_audience`;
};

const Preview = ({
  companySearch,
  personSearch,
  subtype,
  segment,
  disabled,
}) => {
  const [state, setState] = useState('default');
  const [previewId, setPreviewId] = useState(null);
  const [estimatedExternalSize, setEstimatedExternalSize] = useState(null);
  const audienceType = getAudienceType(subtype);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDirty(true);
  }, [companySearch, personSearch, segment]);

  const [createPreview] = useMutation(mutation, {
    variables: {
      input: {
        companySearch,
        personSearch,
        audienceType,
        segmentType: segment?.type,
        segmentSearch: segment?.search,
      },
    },
  });

  const [pollPreview, { data, stopPolling }] = useLazyQuery(query, {
    pollInterval: 1000,
    variables: { id: previewId },
  });

  const {
    startLoading,
    finishLoading,
    ...loadingBarProps
  } = useLoadingBarState({
    onFinishedLoading: () => {
      setState('complete');
    },
    deferLoading: true,
    maxPercentage: 90,
    speed: 'slow',
  });

  const preview = data?.audiencePreview;

  useEffect(() => {
    setDirty(false);

    if (preview?.status === 'complete') {
      stopPolling();
      setEstimatedExternalSize(preview?.estimatedExternalSize);
      finishLoading();
    } else if (preview?.status === 'error') {
      stopPolling();
      setState(preview.status);
    }
  }, [preview?.status]);

  const onClickPreview = () => {
    setState('pending');
    startLoading();
    createPreview().then(({ data: { response: { node } } }) => {
      setPreviewId(node.id);
      pollPreview();
    });
  };

  return (
    <div className="flex">
      {(() => {
        switch (state) {
          case 'default':
            return (
              <CKButton
                isDisabled={disabled}
                leftIcon={<Zap />}
                onClick={onClickPreview}
                variant={CKButtonVariant.tertiary}
                variantColor={CKButtonVariantColor.blue}>
                Calculate audience size
              </CKButton>
            );
          case 'pending':
            return (
              <div className="flex items-center">
                <LoadingBar className="w-20 mr-3" {...loadingBarProps} />
                <span className="text-sm text-gray-600">
                  Calculating audience size...
                </span>
              </div>
            );
          case 'complete':
            if (estimatedExternalSize > 0) {
              return (
                <div className="flex items-center">
                  <div className="mr-3">
                    <HealthIndicator
                      health={audienceHealth(estimatedExternalSize)}
                    />
                  </div>
                  <span className="mr-3 text-sm font-medium">
                    {audienceHealthLabel(estimatedExternalSize)}
                  </span>
                  <span className="mr-3 text-sm text-gray-500">
                    {readableAudienceEstimatedSize(estimatedExternalSize)}{' '}
                    estimated matches
                  </span>
                  <CKButton
                    isDisabled={disabled || !dirty}
                    leftIcon={<Restart />}
                    onClick={onClickPreview}
                    variant={CKButtonVariant.tertiary}
                    variantColor={CKButtonVariantColor.blue}>
                    Recalculate
                  </CKButton>
                </div>
              );
            } else {
              return (
                <div className="flex items-center">
                  <div className="mr-3">
                    <Warning className="text-yellow" />
                  </div>
                  <div className="mr-3 text-sm font-medium">
                    No matches found for this audience
                  </div>
                  <div className="mr-3 text-sm text-gray-500">
                    Use broader criteria
                  </div>
                  <CKButton
                    isDisabled={disabled}
                    variant={CKButtonVariant.tertiary}
                    variantColor={CKButtonVariantColor.blue}
                    leftIcon={<Restart />}
                    onClick={onClickPreview}>
                    Recalculate
                  </CKButton>
                </div>
              );
            }
          case 'error':
            return (
              <div className="flex items-center">
                <div className="mr-3">
                  <Warning className="text-yellow" />
                </div>
                <span className="mr-2 text-sm font-medium">
                  Unable to calculate audience size
                </span>
                <CKButton
                  isDisabled={disabled}
                  leftIcon={<Restart />}
                  onClick={onClickPreview}
                  variant={CKButtonVariant.tertiary}
                  variantColor={CKButtonVariantColor.blue}>
                  Retry
                </CKButton>
              </div>
            );
        }
      })()}
    </div>
  );
};

Preview.propTypes = {
  companySearch: PropTypes.object,
  disabled: PropTypes.bool,
  personSearch: PropTypes.object,
  segment: PropTypes.shape({
    type: PropTypes.string,
    search: PropTypes.string,
  }),
  subtype: PropTypes.string,
};

export default Preview;
